/* eslint @typescript-eslint/strict-boolean-expressions: off */

import { useContext, useRef, useState } from "react";
import { AuthError } from "src/utils/api/auth";
import { CSRFContext } from "src/components/csrf/csrf";
import { firstQueryValue } from "src/utils/links/query";
import { frontend } from "src/utils/env/env";
import { parseBrowserQuery } from "src/utils/links/query";
import styles from "./AuthPages.module.css";
import { InputField } from "src/components/ui-components/Input/InputField";
import { useRouter } from "next/router";
import isEmail from "validator/lib/isEmail";
import { Form } from "src/components/ui-components/Form/Form";
import { Button } from "src/components/ui-components/Button/Button";
import { AnchorButton } from "src/components/ui-components/Button/AnchorButton";
import { useLoginUrl } from "src/hooks/useLoginUrl";
import { httpPost } from "src/utils/http/http";
import Link from "next/link";
import { Logo } from "./Logo";

export function SignUp() {
  const [loading, setLoading] = useState(false);
  const [username, setUsername] = useState("");
  const emailFromUrl = firstQueryValue(parseBrowserQuery(), "email");
  const [email, setEmail] = useState(emailFromUrl || "");
  const [password, setPassword] = useState("");

  const loginUrl = useLoginUrl();

  const [error, setError] = useState<AuthError>();
  const { csrf } = useContext(CSRFContext);
  const { push } = useRouter();
  const emailInputRef = useRef<HTMLInputElement>(null);

  // next is the location that the user should redirect to after registering.
  const next = firstQueryValue(parseBrowserQuery(), "next");

  const onCreateAccount = () => {
    if (!isEmail(email)) {
      if (emailInputRef.current) {
        emailInputRef.current.setCustomValidity(
          `Must be a valid email like "mail@example.com". Must contain "@" and "."`
        );
        emailInputRef.current.reportValidity();
      }
      return;
    }

    const nextPage = new URL("/auth/registerConfirm", frontend());
    nextPage.searchParams.set("username", username);
    if (next !== undefined) {
      // Propogate the post-registration redirect path to the confirmation page.
      nextPage.searchParams.set("next", next);
    }
    setLoading(true);
    register(csrf!, username, password, email)
      .then(() => push(nextPage.pathname + nextPage.search))
      .catch((err) => setError(new AuthError(err.message)))
      .then(() => setLoading(false));
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.logo}>
        <Logo />
      </div>
      <div className={styles.section}>
        <Form size="L" className={styles.form}>
          <h1 className={styles.title}>Sign up</h1>

          <InputField
            size="L"
            style="contained"
            label="Pick a username"
            placeholder="Satoshi"
            onChange={(event) => {
              setUsername(event.target.value);
            }}
            value={username}
            required
          />
          <InputField
            size="L"
            style="contained"
            label="Your email address"
            placeholder="mail@example.com"
            onChange={(event) => {
              setEmail(event.target.value);
              if (isEmail(event.target.value)) {
                event.target.setCustomValidity("");
                event.target.reportValidity();
              }
            }}
            value={email}
            autoComplete="email"
            ref={emailInputRef}
            required
          />
          <InputField
            size="L"
            style="contained"
            label="Choose your password"
            placeholder="Your password..."
            onChange={(event) => {
              setPassword(event.target.value);
            }}
            value={password}
            type="password"
            autoComplete="password"
            minLength={8}
            required
          />

          {error && (
            <p role="alert" className={styles.error}>
              {error.label}
            </p>
          )}
          <Button
            size="L"
            theme="primary"
            className={styles.button}
            onClick={onCreateAccount}
            loading={loading}
            disabled={loading}
          >
            Create account
          </Button>
        </Form>

        <p className={styles.legal}>
          By creating an account you agree to the{" "}
          <Link href="/terms" target="_blank">
            Terms
          </Link>{" "}
          and confirm that you have read the{" "}
          <Link href="/privacy" target="_blank">
            Privacy Policy
          </Link>
          .
        </p>

        <hr />

        <h2 className={styles.subtitle}>Already have an account?</h2>

        <AnchorButton
          size="L"
          theme="primary-light"
          href={loginUrl}
          className={styles.button}
        >
          Sign in
        </AnchorButton>
      </div>
    </div>
  );
}

const register = async (
  csrf: string,
  username: string,
  password: string,
  email: string
) => {
  return httpPost<void>("/api/auth", {
    action: "register",
    csrf,
    username,
    password,
    email,
  });
};
